import React, { FC, HTMLAttributes } from 'react'
import { useState } from 'react'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faChevronSquareDown } from '@fortawesome/pro-light-svg-icons/faChevronSquareDown'
import { faChevronSquareUp } from '@fortawesome/pro-light-svg-icons/faChevronSquareUp'
import { faClose } from '@fortawesome/pro-light-svg-icons/faClose'
import clsx from 'clsx'

import { ActionLink } from '../ActionLink/ActionLink'
import { Button } from '../Button/Button'
import { SemanticIcon } from '../SemanticIcon/SemanticIcon'
import { Text } from '../Text/Text'

export type ItemContent = {
  id?: string
  title?: React.ReactNode
  description?: React.ReactNode
  icon?: IconDefinition
  buttonText?: React.ReactNode
  /**
   * This function is passed as a prop that's triggered when you click the button on each the nudge item.
   */
  clickButton?: (item: ItemContent) => void
  /**
   * This function is passed as a prop that's triggered when you click the dismis button on each the nudge item.
   */
  dismissCallback?: () => void
}

export interface NudgeProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Available options for the nudge
   */
  options: Array<ItemContent>
  theme?: 'neutral' | 'warning' | 'error' | 'success'
  hideDismissButton?: boolean
}

export const NudgeStyle = {
  bottom: 'h-2 mx-2 rounded-b-sm md:hidden',
  control: {
    _: 'flex mt-2 justify-end text-teal-500 cursor-pointer',
    icon: 'ml-2'
  },
  item: {
    _: 'border-l-4 rounded-sm p-4 flex justify-between items-start gap-4',
    actions: 'xs:ml-14 lg:ml-0 lg:ml-auto flex shrink-0 self-start items-center my-auto',
    button: 'mr-4 lg:mr-0 lg:ml-4 order-first lg:order-none',
    dismiss: {
      item: 'my-2 flex cursor-pointer lg:order-first items-center',
      text: 'ml-2'
    },
    information: {
      _: 'xs:flex mb-4 lg:mb-0 lg:mr-4 block self-start',
      text: 'mt-4 xs:mt-0 xs:pl-4'
    }
  },
  theme: {
    error: {
      bottom: 'bg-red-100',
      icon: 'text-error-default',
      item: 'border-error-default bg-error-muted'
    },
    neutral: {
      bottom: 'bg-blue-100',
      icon: 'text-info-default',
      item: 'border-info-default bg-info-muted'
    },
    success: {
      bottom: 'bg-green-100',
      icon: 'text-success-default',
      item: 'border-success-default bg-success-muted'
    },
    warning: {
      bottom: 'bg-yellow-100',
      icon: 'text-warning-default',
      item: 'border-warning-default bg-warning-muted'
    }
  }
}

/**
 * Nudge - Persistent and contextual messages that should be used when we want to guarantee a user reads something and explictly takes action or dismisses the message. This assures they acknowledge the message.
 */
export const Nudge: FC<NudgeProps> = React.forwardRef<HTMLDivElement, NudgeProps>(
  ({ className, hideDismissButton = false, options, theme = 'neutral', ...props }, ref) => {
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [itemOptions, setItemOptions] = useState(options)
    const [headingOption, setHeadingOption] = useState(options[0])

    const dismissItem = (heading: boolean, optionId?: string) => {
      if (heading) {
        setHeadingOption({})
      } else {
        const items = itemOptions?.filter(item => item.id != optionId)
        setItemOptions(items)
      }
    }

    // If all items are dismissed, don't render the component
    if (Object.keys(headingOption).length === 0) {
      return null
    }

    return (
      <div ref={ref} {...props} className={className}>
        {Object.keys(headingOption).length !== 0 ? (
          <div>
            {!isOpen ? (
              <NudgeItem
                clickButton={headingOption.clickButton}
                content={headingOption}
                dismissItem={dismissItem}
                heading={true}
                hideDismissButton={hideDismissButton}
                theme={theme}
              />
            ) : null}
            {itemOptions ? (
              <>
                {!isOpen && itemOptions.length !== 1 ? (
                  <div className={clsx(NudgeStyle.bottom, NudgeStyle.theme[theme].bottom)}></div>
                ) : null}
                {isOpen ? (
                  <div>
                    {itemOptions?.map(option => (
                      <div className='mb-2' key={option.id}>
                        <NudgeItem
                          clickButton={option.clickButton}
                          content={option}
                          dismissItem={dismissItem}
                          heading={false}
                          hideDismissButton={hideDismissButton}
                          theme={theme}
                        />
                      </div>
                    ))}
                  </div>
                ) : null}
                {itemOptions.length > 1 ? (
                  <div onClick={() => setIsOpen(!isOpen)}>
                    <NudgeControl isOpen={isOpen} />
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    )
  }
)

interface NudgeItemProps extends Omit<HTMLAttributes<HTMLDivElement>, 'content'> {
  /**
   * Content of the nudge item
   */
  content: ItemContent
  /**
   * Dismiss the nudge item
   */
  dismissItem: (heading: boolean, optionId: string) => void
  /**
   * Boolean to determine if it's the heading or an individual option that's to be dismissed
   */
  heading: boolean
  /**
   * The function that's triggered when you click the button on the nudge item
   */
  clickButton?: (item: ItemContent) => void
  theme?: 'neutral' | 'warning' | 'error' | 'success'
  hideDismissButton: boolean
}

function NudgeItem({
  clickButton,
  content,
  dismissItem,
  heading,
  hideDismissButton,
  theme = 'neutral'
}: NudgeItemProps) {
  const id: string = content.id || ''
  const buttonAction: (item: ItemContent) => void = clickButton!
  const dismissClickHandle = () => {
    dismissItem(heading, id)
    content.dismissCallback && content.dismissCallback()
  }
  return (
    <div className={clsx(NudgeStyle.item._, NudgeStyle.theme[theme].item)}>
      <div className='justify-between lg:flex'>
        <div className={NudgeStyle.item.information._}>
          {content.icon ? (
            <FontAwesomeIcon className={NudgeStyle.theme[theme].icon} icon={content.icon} size='lg' />
          ) : null}
          <div className={NudgeStyle.item.information.text}>
            <Text as='p' className='mb-0' theme='medium'>
              {content.title}
            </Text>
            <Text as='p' className='mb-0'>
              {content.description}
            </Text>
          </div>
        </div>

        <div className={NudgeStyle.item.actions}>
          {content.buttonText ? (
            <Button className={NudgeStyle.item.button} onClick={() => buttonAction(content)} theme='secondary'>
              {content.buttonText}
            </Button>
          ) : null}
        </div>
      </div>
      {!hideDismissButton ? (
        <button onClick={dismissClickHandle} type='button'>
          <FontAwesomeIcon className='h-6 w-6 text-white' icon={faClose} size='lg' />
        </button>
      ) : null}
    </div>
  )
}

interface NudgeControlProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * The boolean that's used to handle control for expanding and collasping nudge items
   */
  isOpen: boolean
}

function NudgeControl({ isOpen }: NudgeControlProps) {
  return (
    <div className={NudgeStyle.control._}>
      {isOpen ? (
        <>
          <ActionLink icon={faChevronSquareUp} theme='neutral'>
            Collapse
          </ActionLink>
        </>
      ) : (
        <>
          <ActionLink icon={faChevronSquareDown} theme='neutral'>
            View All
          </ActionLink>
        </>
      )}
    </div>
  )
}
